<template>
  <div class="space-y-4">

    <!-- ══════════════════════════════════════════════
         MASTER SWITCH — Système antifraud
    ══════════════════════════════════════════════ -->
    <div :class="['master-switch-card', policyForm.antifraud_enabled === false ? 'master-off' : 'master-on']">
      <div class="master-left">
        <span class="master-icon">
          <i
            :class="policyForm.antifraud_enabled ? 'mdi mdi-shield-check-outline' : 'mdi mdi-cancel'"
            aria-hidden="true"
          ></i>
        </span>
        <div>
          <div class="master-title">Système antifraud</div>
          <div class="master-desc">
            {{ policyForm.antifraud_enabled === false
              ? 'Désactivé — aucune alerte ne sera créée, aucun score ne sera recalculé.'
              : 'Actif — les détections GPS, annulation et notation sont en cours d\'exécution.' }}
          </div>
        </div>
      </div>
      <el-switch
        :model-value="policyForm.antifraud_enabled"
        size="large"
        active-color="#22c55e"
        inactive-color="#ef4444"
        @change="bool('antifraud_enabled', $event)"
      />
    </div>

    <el-alert
      v-if="policyForm.antifraud_enabled === false"
      title="Système antifraud désactivé — aucun artisan ne sera signalé ni suspendu automatiquement tant que cette option est inactive."
      type="error" :closable="false" show-icon class="!rounded-xl"
    />
    <el-alert
      v-else
      title="Ces paramètres s'appliquent immédiatement après sauvegarde et affectent le scoring de tous les artisans actifs."
      type="warning" :closable="false" show-icon class="!rounded-xl"
    />

    <!-- ══════════════════════════════════════════════
         SECTION 1 — Sanctions automatiques
    ══════════════════════════════════════════════ -->
    <div class="policy-card">
      <div class="policy-card-header">
        <span class="hicon"><i class="mdi mdi-scale-balance" aria-hidden="true"></i></span>
        <div>
          <div class="htitle">Sanctions automatiques</div>
          <div class="hdesc">Quand le système agit seul, sans attendre un administrateur.</div>
        </div>
      </div>

      <!-- Preset rapide -->
      <div class="preset-row">
        <button v-for="p in SANCTION_PRESETS" :key="p.key"
          :class="['preset-btn', { active: activeSanctionPreset === p.key }]"
          :disabled="!canEdit"
          @click="applySanctionPreset(p)">
          {{ p.label }}
        </button>
      </div>

      <div class="settings-list">

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Mise sous surveillance</div>
            <div class="sdesc">L'artisan est signalé pour examen manuel dès que son score de risque dépasse ce seuil.</div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">Permissif</span>
              <el-slider :model-value="policyForm.review_risk_score"
                :min="0.5" :max="10" :step="0.5"
                :format-tooltip="v => `Score ${v}`"
                @change="num('review_risk_score', $event)" class="flex-1" />
              <span class="slabel">Strict</span>
              <span class="val-badge">{{ policyForm.review_risk_score }}</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Suspension automatique</div>
            <div class="sdesc">L'artisan est suspendu immédiatement sans action manuelle. Doit être supérieur au seuil de surveillance.</div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">Facile</span>
              <el-slider :model-value="policyForm.suspend_risk_score"
                :min="1.0" :max="15" :step="0.5"
                :format-tooltip="v => `Score ${v}`"
                @change="num('suspend_risk_score', $event)" class="flex-1" />
              <span class="slabel">Difficile</span>
              <span class="val-badge">{{ policyForm.suspend_risk_score }}</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Alertes confirmées → Suspension directe</div>
            <div class="sdesc">Si un admin confirme ce nombre d'alertes, l'artisan est suspendu même si son score est bas. Mettre à 0 pour désactiver.</div>
          </div>
          <div class="setting-control">
            <div class="flex items-center gap-3">
              <el-input-number :model-value="policyForm.suspend_confirmed_alerts"
                :min="0" :max="10" @change="num('suspend_confirmed_alerts', $event)" class="!w-28" />
              <span class="text-sm text-profissa-text-muted">alerte(s) confirmée(s)</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Anti-doublon d'alertes</div>
            <div class="sdesc">Délai minimum avant qu'une même alerte puisse se répéter pour le même artisan. Évite le spam d'alertes.</div>
          </div>
          <div class="setting-control">
            <el-select :model-value="String(policyForm.fraud_alert_cooldown_minutes)"
              @change="num('fraud_alert_cooldown_minutes', Number($event))" class="!w-40">
              <el-option value="15" label="15 minutes" />
              <el-option value="30" label="30 minutes" />
              <el-option value="60" label="1 heure" />
              <el-option value="120" label="2 heures" />
              <el-option value="360" label="6 heures" />
            </el-select>
          </div>
        </div>

      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         SECTION 2 — Arrivée hors zone
    ══════════════════════════════════════════════ -->
    <div :class="['policy-card', policyForm.detect_arrival_out_of_zone === false && 'card-disabled']">
      <div class="policy-card-header">
        <span class="hicon"><i class="mdi mdi-map-marker-radius-outline" aria-hidden="true"></i></span>
        <div class="flex-1">
          <div class="htitle">Arrivée hors zone</div>
          <div class="hdesc">Alerte quand un artisan valide son arrivée alors qu'il est trop loin de l'adresse du client. Souvent dû à un GPS imprécis — choisissez un seuil raisonnable (≥ 100 m).</div>
        </div>
        <el-switch
          :model-value="policyForm.detect_arrival_out_of_zone"
          :disabled="policyForm.antifraud_enabled === false"
          size="small"
          @change="bool('detect_arrival_out_of_zone', $event)"
        />
      </div>

      <div class="settings-list">
        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Distance maximale pour une arrivée valide</div>
            <div class="sdesc">Au-delà de cette distance du client, la validation d'arrivée déclenche une alerte (sans bloquer la mission).</div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">50 m</span>
              <el-slider :model-value="policyForm.arrival_distance_meters"
                :min="50" :max="500" :step="25" :format-tooltip="v => `${v} m`"
                @change="num('arrival_distance_meters', $event)" class="flex-1" />
              <span class="slabel">500 m</span>
              <span class="val-badge">{{ policyForm.arrival_distance_meters }} m</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Sévérité sur le score de risque</div>
            <div class="sdesc">Ce signal est souvent lié à une imprécision GPS (bâtiment, réseau faible). Recommandé : Faible ou Modérée.</div>
          </div>
          <div class="setting-control">
            <SeverityPills :weight="policyForm.weight_arrival_out_of_zone" @change="num('weight_arrival_out_of_zone', $event)" />
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         SECTION 4 — Fausse annulation
    ══════════════════════════════════════════════ -->
    <div :class="['policy-card', policyForm.detect_fake_cancellation === false && 'card-disabled']">
      <div class="policy-card-header">
        <span class="hicon"><i class="mdi mdi-cancel" aria-hidden="true"></i></span>
        <div class="flex-1">
          <div class="htitle">Fausse annulation</div>
          <div class="hdesc">Détecte quand un artisan reste longtemps près d'un client qui finit par annuler — comportement qui peut indiquer une pression exercée sur le client.</div>
        </div>
        <el-switch
          :model-value="policyForm.detect_fake_cancellation"
          :disabled="policyForm.antifraud_enabled === false"
          size="small"
          @change="bool('detect_fake_cancellation', $event)"
        />
      </div>

      <div class="settings-list">
        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Durée minimale de présence suspecte</div>
            <div class="sdesc">L'artisan doit avoir été à proximité pendant au moins ce temps pour que l'annulation déclenche une alerte.</div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">2 min</span>
              <el-slider :model-value="policyForm.fake_cancellation_min_stay_minutes"
                :min="2" :max="30" :step="1" :format-tooltip="v => `${v} min`"
                @change="num('fake_cancellation_min_stay_minutes', $event)" class="flex-1" />
              <span class="slabel">30 min</span>
              <span class="val-badge">{{ policyForm.fake_cancellation_min_stay_minutes }} min</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Rayon de présence comptabilisé</div>
            <div class="sdesc">La présence de l'artisan n'est comptée que s'il est dans ce rayon autour du client.</div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">50 m</span>
              <el-slider :model-value="policyForm.fake_cancellation_distance_meters"
                :min="50" :max="300" :step="25" :format-tooltip="v => `${v} m`"
                @change="num('fake_cancellation_distance_meters', $event)" class="flex-1" />
              <span class="slabel">300 m</span>
              <span class="val-badge">{{ policyForm.fake_cancellation_distance_meters }} m</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Sévérité sur le score de risque</div>
            <div class="sdesc">Signal fort de comportement abusif. Recommandé : Sévère ou Critique.</div>
          </div>
          <div class="setting-control">
            <SeverityPills :weight="policyForm.weight_fake_cancellation" @change="num('weight_fake_cancellation', $event)" />
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         SECTION 4 — Fenêtre temporelle (décroissance)
    ══════════════════════════════════════════════ -->
    <div class="policy-card">
      <div class="policy-card-header">
        <span class="hicon">⏳</span>
        <div>
          <div class="htitle">Fenêtre temporelle — décroissance des alertes</div>
          <div class="hdesc">
            Le poids d'une alerte ancienne diminue automatiquement avec le temps. Passé la demi-vie,
            son impact est divisé par deux. Un artisan sans nouvelle alerte verra son score baisser progressivement.
          </div>
        </div>
      </div>

      <div class="settings-list">

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Demi-vie — alertes ouvertes / en enquête</div>
            <div class="sdesc">
              Après ce nombre de jours, une alerte non confirmée pèse deux fois moins.
              Réduire cette valeur favorise le pardon rapide ; augmenter la rend plus durable.
            </div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">30 j</span>
              <el-slider
                :model-value="policyForm.risk_score_half_life_days"
                :min="30" :max="730" :step="30"
                :format-tooltip="v => `${v} jours`"
                @change="num('risk_score_half_life_days', $event)"
                class="flex-1"
              />
              <span class="slabel">2 ans</span>
              <span class="val-badge">{{ policyForm.risk_score_half_life_days }} j</span>
            </div>
            <div class="decay-preview">
              <span>À 6 mois : ×{{ halfLifeAtMonth(policyForm.risk_score_half_life_days, 6) }}</span>
              <span>À 1 an : ×{{ halfLifeAtMonth(policyForm.risk_score_half_life_days, 12) }}</span>
              <span>À 2 ans : ×{{ halfLifeAtMonth(policyForm.risk_score_half_life_days, 24) }}</span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Demi-vie — alertes confirmées par un admin</div>
            <div class="sdesc">
              Les alertes confirmées représentent une fraude avérée. Leur demi-vie est plus longue
              pour que les cas graves restent visibles même avec le temps.
            </div>
          </div>
          <div class="setting-control">
            <div class="slider-row">
              <span class="slabel">90 j</span>
              <el-slider
                :model-value="policyForm.risk_score_confirmed_half_life_days"
                :min="90" :max="1095" :step="30"
                :format-tooltip="v => `${v} jours`"
                @change="num('risk_score_confirmed_half_life_days', $event)"
                class="flex-1"
              />
              <span class="slabel">3 ans</span>
              <span class="val-badge">{{ policyForm.risk_score_confirmed_half_life_days }} j</span>
            </div>
            <div class="decay-preview">
              <span>À 1 an : ×{{ halfLifeAtMonth(policyForm.risk_score_confirmed_half_life_days, 12) }}</span>
              <span>À 2 ans : ×{{ halfLifeAtMonth(policyForm.risk_score_confirmed_half_life_days, 24) }}</span>
              <span>À 3 ans : ×{{ halfLifeAtMonth(policyForm.risk_score_confirmed_half_life_days, 36) }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         SECTION AI SHIELD
    ══════════════════════════════════════════════ -->
    <div :class="['policy-card', policyForm.ai_shield_enabled === false && 'card-disabled']">
      <div class="policy-card-header">
        <span class="hicon"><i class="mdi mdi-robot-outline" aria-hidden="true"></i></span>
        <div class="flex-1">
          <div class="htitle">AI Shield</div>
          <div class="hdesc">Analyse automatique des devis, photos et identités via IA (Claude / Gemini). Désactiver réduit les coûts API mais supprime la détection intelligente.</div>
        </div>
        <el-switch
          :model-value="policyForm.ai_shield_enabled"
          size="large"
          active-color="#6366f1"
          @change="bool('ai_shield_enabled', $event)"
        />
      </div>

      <div class="settings-list">

        <!-- Prix -->
        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Analyse des prix de devis</div>
            <div class="sdesc">Détecte les articles dont le prix dépasse le benchmark marché de plus du seuil configuré. Alerte fraud créée automatiquement.</div>
          </div>
          <div class="setting-control">
            <div class="flex items-center gap-3">
              <el-switch
                :model-value="policyForm.ai_shield_price_check"
                :disabled="policyForm.ai_shield_enabled === false"
                @change="bool('ai_shield_price_check', $event)"
              />
              <span class="text-sm" :class="policyForm.ai_shield_price_check ? 'text-green-400' : 'text-profissa-text-muted'">
                {{ policyForm.ai_shield_price_check ? 'Actif' : 'Inactif' }}
              </span>
            </div>
            <div v-if="policyForm.ai_shield_enabled !== false && policyForm.ai_shield_price_check !== false" class="mt-3">
              <div class="slider-row">
                <span class="slabel">10%</span>
                <el-slider :model-value="policyForm.price_benchmark_threshold * 100"
                  :min="10" :max="100" :step="5"
                  :format-tooltip="v => `${v}%`"
                  @change="num('price_benchmark_threshold', $event / 100)" class="flex-1" />
                <span class="slabel">100%</span>
                <span class="val-badge">{{ Math.round(policyForm.price_benchmark_threshold * 100) }}%</span>
              </div>
              <div class="text-xs text-profissa-text-muted mt-1">Écart max autorisé par rapport au prix marché avant alerte.</div>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <!-- Photos -->
        <div class="setting-item">
          <div class="setting-info">
            <div class="sname">Analyse des photos avant/après</div>
            <div class="sdesc">Compare les photos prises avant et après l'intervention pour détecter des photos falsifiées ou non cohérentes avec le travail déclaré.</div>
          </div>
          <div class="setting-control">
            <div class="flex items-center gap-3">
              <el-switch
                :model-value="policyForm.ai_shield_photo_check"
                :disabled="policyForm.ai_shield_enabled === false"
                @change="bool('ai_shield_photo_check', $event)"
              />
              <span class="text-sm" :class="policyForm.ai_shield_photo_check ? 'text-green-400' : 'text-profissa-text-muted'">
                {{ policyForm.ai_shield_photo_check ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>
        </div>

        <div class="setting-divider" />

        <!-- OCR CNIB -->
      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         RÉSUMÉ D'IMPACT
    ══════════════════════════════════════════════ -->
    <div class="impact-box">
      <div class="impact-title">
        <i class="mdi mdi-chart-box-outline mr-2" aria-hidden="true"></i>
        Résumé d'impact — ce que signifient ces réglages
      </div>
      <div class="impact-body">
        <div class="impact-thresholds">
          <div class="impact-threshold-item review">
            <div class="it-dot"></div>
            <div>
              <span class="it-action">Mise sous surveillance</span> dès qu'un artisan atteint un score de risque de
              <strong>{{ policyForm.review_risk_score }}</strong>
            </div>
          </div>
          <div class="impact-threshold-item suspend">
            <div class="it-dot"></div>
            <div>
              <span class="it-action">Suspension automatique</span> si le score dépasse
              <strong>{{ policyForm.suspend_risk_score }}</strong>
              <span v-if="policyForm.suspend_confirmed_alerts > 0">
                ou après <strong>{{ policyForm.suspend_confirmed_alerts }}</strong> alerte(s) confirmée(s)
              </span>
            </div>
          </div>
        </div>

        <div class="impact-examples">
          <div class="impact-examples-title">Contribution au score par alerte :</div>
          <div v-for="ex in impactExamples" :key="ex.label" class="impact-example-row">
            <span class="ex-label">{{ ex.label }}</span>
            <div class="ex-scores">
              <span class="ex-score-item">
                <span class="ex-sl">Ouverte</span>
                <span :class="['ex-sv', scoreClass(ex.open)]">+{{ ex.open }}</span>
              </span>
              <span class="ex-score-item">
                <span class="ex-sl">Confirmée</span>
                <span :class="['ex-sv', scoreClass(ex.confirmed)]">+{{ ex.confirmed }}</span>
              </span>
            </div>
          </div>
          <div class="impact-note">
            Ouverte × 0.5 · En enquête × 0.7 · Confirmée × 1.0 · scores valables pour une alerte récente (décroissance = 1.0)
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         ACTIONS
    ══════════════════════════════════════════════ -->
    <div class="flex gap-3 pt-1">
      <el-button class="flex-1 h-10" @click="resetDefaults">
        Réinitialiser les valeurs recommandées
      </el-button>
      <el-button type="primary" :loading="isSaving" :disabled="!canEdit" class="flex-1 h-12 text-base font-bold" @click="$emit('save')">
        Appliquer et déployer
      </el-button>
    </div>

  </div>
</template>

<script setup>
import { computed, defineComponent, h, defineProps, defineEmits } from 'vue'

const props = defineProps({
  policyForm: { type: Object,  required: true },
  isSaving:   { type: Boolean, default: false },
  canEdit:    { type: Boolean, default: false },
})

const emit = defineEmits(['save', 'update:policyForm'])

// ─── Helpers ─────────────────────────────────────────────────────────────────

const num  = (field, val) => {
  if (!props.canEdit) return
  emit('update:policyForm', { ...props.policyForm, [field]: Number(val) })
}

const bool = (field, val) => {
  if (!props.canEdit) return
  emit('update:policyForm', { ...props.policyForm, [field]: Boolean(val) })
}

// ─── Severity pills sub-component ────────────────────────────────────────────

const SEVERITIES = [
  { key: 'off',      label: 'Ignorée',  weight: 0,   color: '#9ca3af' },
  { key: 'low',      label: 'Faible',   weight: 0.5, color: '#3b82f6' },
  { key: 'medium',   label: 'Modérée',  weight: 1.5, color: '#f59e0b' },
  { key: 'high',     label: 'Sévère',   weight: 3.0, color: '#f97316' },
  { key: 'critical', label: 'Critique', weight: 5.0, color: '#ef4444' },
]

const weightToKey = (w) => {
  if (w <= 0.01) return 'off'
  if (w <= 0.9)  return 'low'
  if (w <= 2.2)  return 'medium'
  if (w <= 4.0)  return 'high'
  return 'critical'
}

const SeverityPills = defineComponent({
  props: { weight: { type: Number, default: 0 } },
  emits: ['change'],
  setup(p, { emit: e }) {
    return () =>
      h('div', { class: 'severity-pills' },
        SEVERITIES.map(s => {
          const active = weightToKey(p.weight) === s.key
          return h('button', {
            key: s.key,
            class: ['severity-pill', { 'sev-active': active }],
            style: active ? { backgroundColor: s.color, borderColor: s.color, color: '#fff' } : {},
            onClick: () => e('change', s.weight),
          }, s.label)
        })
      )
  },
})

// ─── Sanction presets ─────────────────────────────────────────────────────────

const SANCTION_PRESETS = [
  { key: 'permissive', label: 'Permissif',  review: 5.0, suspend: 10.0, alerts: 3 },
  { key: 'standard',   label: 'Standard',   review: 3.0, suspend: 6.0,  alerts: 2 },
  { key: 'strict',     label: 'Strict',     review: 1.5, suspend: 3.0,  alerts: 1 },
]

const activeSanctionPreset = computed(() => {
  const p = props.policyForm
  const match = SANCTION_PRESETS.find(
    preset =>
      preset.review === p.review_risk_score &&
      preset.suspend === p.suspend_risk_score &&
      preset.alerts === p.suspend_confirmed_alerts,
  )
  return match?.key ?? null
})

const applySanctionPreset = (preset) => {
  if (!props.canEdit) return
  emit('update:policyForm', {
    ...props.policyForm,
    review_risk_score: preset.review,
    suspend_risk_score: preset.suspend,
    suspend_confirmed_alerts: preset.alerts,
  })
}

// ─── Impact summary ───────────────────────────────────────────────────────────

const impactExamples = computed(() => {
  const f = props.policyForm
  return [
    { label: 'Fausse annulation',    w: f.weight_fake_cancellation },
    { label: 'Arrivée hors zone',    w: f.weight_arrival_out_of_zone },
  ].map(e => ({
    label:     e.label,
    open:      (e.w * 0.5).toFixed(2),
    confirmed: (e.w * 1.0).toFixed(2),
    weight:    e.w,
  }))
})

// Decay preview: what fraction of the original weight remains after N months
const halfLifeAtMonth = (halfLifeDays, months) => {
  const ageDays = months * 30.44
  return Math.pow(0.5, ageDays / halfLifeDays).toFixed(2)
}

const scoreClass = (val) => {
  const v = parseFloat(val)
  if (v === 0) return 'sv-zero'
  const f = props.policyForm
  if (v >= f.suspend_risk_score) return 'sv-danger'
  if (v >= f.review_risk_score)  return 'sv-warn'
  return 'sv-ok'
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULTS = {
  antifraud_enabled:                 true,
  ai_shield_enabled:                 true,
  detect_arrival_out_of_zone:        true,
  detect_fake_cancellation:          true,
  ai_shield_price_check:             true,
  ai_shield_photo_check:             true,
  gps_update_interval_seconds:       60,
  arrival_distance_meters:           300,
  proximity_alert_distance_meters:   50,
  proximity_min_duration_minutes:    15,
  fraud_alert_cooldown_minutes:      30,
  fake_cancellation_min_stay_minutes: 120,
  fake_cancellation_distance_meters:  120,
  review_risk_score:                 3.0,
  suspend_risk_score:                6.0,
  suspend_confirmed_alerts:          2,
  weight_fake_cancellation:          3.0,
  weight_arrival_out_of_zone:        2.0,
  ai_provider:                       'claude',
  price_benchmark_threshold:         0.30,
  risk_score_half_life_days:         180,
  risk_score_confirmed_half_life_days: 365,
}

const resetDefaults = () => emit('update:policyForm', { ...DEFAULTS })
</script>

<style scoped>
/* ── Master switch card ─────────────────────────────────────────────── */
.master-switch-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 2px solid;
  transition: all 0.2s ease;
}
.master-switch-card.master-on  { border-color: rgba(34,197,94,0.4); background: rgba(34,197,94,0.06); }
.master-switch-card.master-off { border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.06); }
.master-left { display: flex; align-items: center; gap: 0.75rem; flex: 1; min-width: 0; }
.master-icon { font-size: 1.75rem; flex-shrink: 0; }
.master-title { font-weight: 700; font-size: 1rem; color: var(--text-main); }
.master-desc  { font-size: 0.8125rem; color: var(--text-muted); margin-top: 0.25rem; line-height: 1.4; }

/* ── Disabled card ──────────────────────────────────────────────────── */
.card-disabled { opacity: 0.55; }
.card-disabled .settings-list,
.card-disabled .preset-row { pointer-events: none; opacity: 0.6; }

/* ── Cards ─────────────────────────────────────────────────────────── */
.policy-card {
  border-radius: 1rem;
  border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
  background: var(--glass-bg, rgba(255,255,255,0.03));
  overflow: hidden;
}
.policy-card-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.25rem;
  border-bottom: 1px solid var(--glass-border, rgba(255,255,255,0.08));
}
.hicon { font-size: 1.5rem; margin-top: 0.125rem; flex-shrink: 0; }
.htitle { font-family: var(--font-display, inherit); font-weight: 700; font-size: 0.9375rem; color: var(--text-main); }
.hdesc { font-size: 0.8125rem; color: var(--text-muted); margin-top: 0.25rem; line-height: 1.5; }

/* ── Settings list ─────────────────────────────────────────────────── */
.settings-list { border: none; }
.settings-list > .setting-item + .setting-item {
  border-top: 1px solid var(--glass-border, rgba(255,255,255,0.08));
}
.setting-item {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.25rem;
}
@media (min-width: 768px) {
  .setting-item { flex-direction: row; align-items: center; }
  .setting-control { width: 16rem; flex-shrink: 0; }
}
.setting-info { flex: 1; min-width: 0; }
.sname { font-weight: 600; font-size: 0.875rem; color: var(--text-main); }
.sdesc { font-size: 0.75rem; color: var(--text-muted); margin-top: 0.25rem; line-height: 1.5; }

/* ── Slider ─────────────────────────────────────────────────────────── */
.slider-row { display: flex; align-items: center; gap: 0.5rem; }
.slabel {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-dim);
  flex-shrink: 0;
  width: 3rem;
  text-align: center;
}
.val-badge {
  font-size: 0.75rem;
  font-weight: 900;
  color: var(--text-main);
  border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
  border-radius: 0.5rem;
  padding: 0.125rem 0.5rem;
  flex-shrink: 0;
  min-width: 3.5rem;
  text-align: center;
}

/* ── Severity pills ─────────────────────────────────────────────────── */
:deep(.severity-pills) { display: flex; gap: 0.25rem; flex-wrap: wrap; }
:deep(.severity-pill) {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--glass-border, rgba(255,255,255,0.12));
  color: var(--text-muted);
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
:deep(.severity-pill:hover) { border-color: var(--text-dim); }
:deep(.sev-active) { box-shadow: 0 1px 4px rgba(0,0,0,0.2); }

/* ── Preset buttons ────────────────────────────────────────────────── */
.preset-row { display: flex; gap: 0.5rem; padding: 1rem 1.25rem 0; }
.preset-btn {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
  color: var(--text-muted);
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.preset-btn:hover { border-color: var(--text-dim); }
.preset-btn.active {
  background: rgba(255, 107, 0, 0.1);
  border-color: var(--accent, #ff6b00);
  color: var(--accent, #ff6b00);
}

/* ── Experimental badge ─────────────────────────────────────────────── */
.exp-badge {
  font-size: 0.6rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.3);
  color: #eab308;
}

/* ── Impact box ─────────────────────────────────────────────────────── */
.impact-box {
  border-radius: 1rem;
  border: 1px solid rgba(59, 130, 246, 0.2);
  background: rgba(59, 130, 246, 0.05);
  padding: 1.25rem;
}
.impact-title { font-weight: 700; font-size: 0.875rem; color: var(--text-main); margin-bottom: 0.75rem; }
.impact-body { display: flex; flex-direction: column; gap: 1rem; }

.impact-thresholds { display: flex; flex-direction: column; gap: 0.5rem; }
.impact-threshold-item { display: flex; align-items: flex-start; gap: 0.75rem; font-size: 0.875rem; color: var(--text-muted); }
.it-dot { width: 0.625rem; height: 0.625rem; border-radius: 9999px; flex-shrink: 0; margin-top: 0.3rem; }
.impact-threshold-item.review .it-dot { background: #eab308; }
.impact-threshold-item.suspend .it-dot { background: #ef4444; }
.it-action { font-weight: 700; color: var(--text-main); }

.impact-examples { display: flex; flex-direction: column; gap: 0.5rem; }
.impact-examples-title {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-dim);
  margin-bottom: 0.25rem;
}
.impact-example-row { display: flex; align-items: center; gap: 0.75rem; }
.ex-label { font-size: 0.75rem; color: var(--text-muted); flex: 1; }
.ex-scores { display: flex; gap: 0.75rem; }
.ex-score-item { display: flex; align-items: center; gap: 0.25rem; }
.ex-sl { font-size: 0.65rem; color: var(--text-dim); }
.ex-sv { font-size: 0.75rem; font-weight: 900; }
.sv-zero   { color: var(--text-dim); }
.sv-ok     { color: #22c55e; }
.sv-warn   { color: #eab308; }
.sv-danger { color: #ef4444; }
.impact-note { font-size: 0.65rem; color: var(--text-dim); font-weight: 600; margin-top: 0.5rem; }

/* ── Decay preview row ──────────────────────────────────────────────── */
.decay-preview {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-dim);
  flex-wrap: wrap;
}
.decay-preview span {
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
  border-radius: 0.5rem;
  padding: 0.125rem 0.5rem;
}
</style>
